// FEAT-051 — Voice Profile document analysis modal.
//
// Shared by VoiceProfileEditor's "Analyze a document" action (enrich an
// existing profile, S-296) and the no-profile create flow (S-297). This
// component only extracts — it calls POST /voice-profiles/analyze and
// hands the result to the caller's onExtracted callback. It never writes
// to a voice profile itself; the caller decides how to apply the fields
// (populate an existing form vs. seed a new one) and the founder still
// has to hit Save — nothing here is persisted automatically.

import { useState } from "react";
import { useApi } from "@/lib/api";
import { ModalShell } from "@/components/SettingsModalShell";

export interface AnalyzedVoiceFields {
  core_identity: string;
  hard_rules: string[];
  strong_tendencies: string[];
  light_preferences: string[];
  anti_ai_patterns: string[];
  top_3_rules: string[];
  litmus_test: string | null;
  source_markdown: string;
}

type Mode = "file" | "text";

export function AnalyzeVoiceDocumentModal({
  onClose,
  onExtracted,
}: {
  onClose: () => void;
  onExtracted: (fields: AnalyzedVoiceFields, sourceLabel: string) => void;
}) {
  const api = useApi();
  const [mode, setMode] = useState<Mode>("file");
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);
    setBusy(true);
    try {
      let fields: AnalyzedVoiceFields;
      let sourceLabel: string;
      if (mode === "file") {
        if (!file) throw new Error("Choose a file to upload.");
        const form = new FormData();
        form.append("file", file);
        fields = await api<AnalyzedVoiceFields>("/voice-profiles/analyze", {
          method: "POST",
          body: form,
        });
        sourceLabel = file.name;
      } else {
        if (!text.trim()) throw new Error("Paste some text.");
        fields = await api<AnalyzedVoiceFields>("/voice-profiles/analyze", {
          method: "POST",
          body: JSON.stringify({ text }),
        });
        sourceLabel = "pasted text";
      }
      onExtracted(fields, sourceLabel);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Analysis failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title="Analyze a document" eyebrow="Voice profile" onClose={onClose}>
      <p className="settings-label-hint" style={{ marginBottom: "1rem" }}>
        Upload or paste a voice document. Fields populate the form below —
        nothing is saved until you review and hit Save.
      </p>

      <div
        className="settings-toggle"
        style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}
      >
        {(["file", "text"] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            className="settings-input"
            style={{
              width: "auto",
              fontWeight: mode === m ? 700 : 400,
              background: mode === m ? "var(--accent, #0969da)" : undefined,
              color: mode === m ? "#fff" : undefined,
            }}
            onClick={() => setMode(m)}
          >
            {m === "file" ? "Upload file" : "Paste text"}
          </button>
        ))}
      </div>

      {mode === "file" && (
        <div className="settings-field">
          <label className="settings-label">File (PDF, .docx, .txt, .md — max 25 MB)</label>
          <input
            type="file"
            className="settings-input"
            accept=".pdf,.docx,.txt,.md"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
      )}

      {mode === "text" && (
        <div className="settings-field">
          <label className="settings-label">Pasted text</label>
          <textarea
            className="voice-textarea"
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste the voice document's content here."
          />
        </div>
      )}

      {err && <div className="settings-field-error">{err}</div>}

      <div className="settings-form-actions" style={{ marginTop: "1rem" }}>
        <button type="button" className="btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={busy}
          onClick={() => void submit()}
        >
          {busy ? "Analyzing…" : "Analyze"}
        </button>
      </div>
    </ModalShell>
  );
}
